import { useLocalStorage } from '@mantine/hooks'
import { useCallback, useEffect, useRef } from 'react'
import type { QuestV2 } from './types'

const CARD_SEEN_KEY = 'quest-v2:card-seen'
const TAB_LAST_SEEN_KEY = 'quest-v2:tab-last-seen'

// ---------------------------------------------------------------------------
// Card-level dot
// ---------------------------------------------------------------------------

/**
 * Manages notification dot for a single quest card.
 *
 * - `follow_url` / `participate_survey` / `open_download_app_modal`: dismissed on click (call `markSeen`)
 * - `claim_reward`: dismissed when card enters viewport
 * - `rewarded` quests never show a dot
 */
export function useQuestCardDot(quest: QuestV2) {
  const [seenMap, setSeenMap] = useLocalStorage<Record<string, boolean>>({
    key: CARD_SEEN_KEY,
    defaultValue: {},
  })

  const seen = seenMap[quest.id] ?? false
  const showDot = !seen && quest.status !== 'rewarded'

  const markSeen = useCallback(() => {
    setSeenMap(prev => ({ ...prev, [quest.id]: true }))
  }, [quest.id, setSeenMap])

  // Auto-dismiss for claim_reward when visible
  const viewRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (quest.buttonAction !== 'claim_reward' || !showDot) return
    const el = viewRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          markSeen()
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [quest.buttonAction, showDot, markSeen])

  return { showDot, markSeen, viewRef }
}

// ---------------------------------------------------------------------------
// Tab-level dot
// ---------------------------------------------------------------------------

/**
 * Detects whether a category tab has "new" quests.
 *
 * Uses the NotificationNews timestamp pattern:
 * store last-seen timestamp per category, compare against newest quest's
 * `createdAt` in that category.
 */
export function useCategoryDots(questsByCategory: Map<string, QuestV2[]>) {
  const [lastSeenMap, setLastSeenMap] = useLocalStorage<Record<string, number>>(
    {
      key: TAB_LAST_SEEN_KEY,
      defaultValue: {},
    },
  )

  const hasDot = useCallback(
    (categoryKey: string): boolean => {
      const quests = questsByCategory.get(categoryKey)
      if (!quests?.length) return false
      const lastSeen = lastSeenMap[categoryKey] ?? 0
      return quests.some(
        q =>
          q.status !== 'rewarded' && new Date(q.createdAt).getTime() > lastSeen,
      )
    },
    [questsByCategory, lastSeenMap],
  )

  const markCategorySeen = useCallback(
    (categoryKey: string) => {
      setLastSeenMap(prev => ({
        ...prev,
        [categoryKey]: Date.now(),
      }))
    },
    [setLastSeenMap],
  )

  return { hasDot, markCategorySeen }
}
